// eslint-disable-next-line monorepo/no-internal-import
import {PageReadyHint} from '@aboutyou/bootstrap/src/pageReadyHint/PageReadyHint';
import {useIsSoftFeatureToggleActive} from '@aboutyou/feature-flags';
import {
  ActivateExcludeAssortmentABTest,
  ActivateWebCouponInOutdatedBasket,
  BrowserMemoryConsumptionTracker,
  CheckoutPreloadConditional,
  CountryDetection,
  CouponTopBanner,
  DatadogLogsRouteTracker,
  DisableAddToHomeScreen,
  DiscountBanner,
  DiscountStickyBar,
  DiscountsRefresh,
  FraudDetection,
  GuestCheckoutSessionRemover,
  HelpCenterChatbotOpener,
  InitializeAbTestsConditionally,
  SearchResultQuestionnaire,
  SessionSharing,
  SlideInWebCouponBanner,
  SubscribeToWhatsappNewsletter,
  SyncSessionBetweenTabs,
  UnlockCouponInfo,
  UspBanner,
  useFullScreenView,
  useIsSpecialPageWithoutUspBanner,
  useShowSlideInWebCouponBanner,
} from '@aboutyou/logic-components';
import {
  useHistory,
  useIsBasketPage,
  useIsCheckoutPage,
  useIsOrderSuccessPage,
} from '@aboutyou/router';
import {TrackCustomer, TrackSearch} from '@aboutyou/tracking';
import {
  ClientGate,
  ErrorBoundary,
  ModalDialog,
  ScrollToTop,
  SkipToContentLink,
  useHydrationDone,
} from '@aboutyou/ui-components';
import React, {useRef} from 'react';
import {ConditionalSyncLegacyState} from '../bridge/SyncLegacyStateContext';
import {PhonePopupConditional} from '../bridge/global/PhonePopup/PhonePopup';
import {SettingsModal} from '../bridge/global/SettingsModal/SettingsModal';
import {ToastNotifications} from '../bridge/global/ToastNotifications/ToastNotifications';
import {UpdateCampaigns} from '../bridge/global/UpdateCampaigns/UpdateCampaigns';
import {AppProviders} from './AppProviders';
import {RouteComponents} from './RouteComponents';
import {Footer} from './components/globals/Footer';
import {Header} from './components/globals/Header/Header';

export const App: React.FC = () => {
  return (
    <AppProviders>
      <DisableAddToHomeScreen />
      <SkipToContentLink />
      <DiscountsRefresh />

      <ErrorBoundary renderContext="header_coupon_banner">
        <ConditionalCouponTopBanner />
      </ErrorBoundary>
      <ErrorBoundary renderContext="header_campaign_banner">
        <ConditionalDiscountBanner />
      </ErrorBoundary>

      <ConditionalHeader />
      <ConditionalUspBanner />
      <ErrorBoundary renderContext="route_components">
        <RouteComponents />
      </ErrorBoundary>
      <ConditionalFooter />

      <ClientGate>
        <ActivateExcludeAssortmentABTest />
        <ErrorBoundary renderContext="unlock_coupon" ErrorComponent={null}>
          <UnlockCouponInfo LayoutComponent={ModalDialog} />
        </ErrorBoundary>
        <SyncSessionBetweenTabs />
        <DiscountStickyBar />
        <ErrorBoundary renderContext="country_detection" ErrorComponent={null}>
          <CountryDetectionConditionally />
        </ErrorBoundary>
        <ErrorBoundary
          renderContext="search_results_questionnaire"
          ErrorComponent={null}
        >
          <SearchResultQuestionnaire />
        </ErrorBoundary>

        <ErrorBoundary renderContext="web_coupon_banner" ErrorComponent={null}>
          <ConditionalSlideInWebCouponBanner />
        </ErrorBoundary>

        <ErrorBoundary renderContext="scroll_top" ErrorComponent={null}>
          <ScrollToTop position="right" />
        </ErrorBoundary>

        <ErrorBoundary renderContext="track_customer" ErrorComponent={null}>
          <TrackCustomer />
        </ErrorBoundary>

        <ErrorBoundary renderContext="track_search" ErrorComponent={null}>
          <TrackSearch />
        </ErrorBoundary>
        <ConditionalPhonePopup />

        <ErrorBoundary renderContext="session_sharing" ErrorComponent={null}>
          <SessionSharing />
        </ErrorBoundary>
        <ErrorBoundary renderContext="legacy_state_sync" ErrorComponent={null}>
          <ConditionalSyncLegacyState>
            {({mountLegacyComponents}) => {
              return mountLegacyComponents ? (
                <>
                  <SettingsModal />
                  <ToastNotifications />
                  <UpdateCampaigns />
                </>
              ) : null;
            }}
          </ConditionalSyncLegacyState>
        </ErrorBoundary>
        <ErrorBoundary
          renderContext="activate_web_coupon_in_outdated_basket"
          ErrorComponent={null}
        >
          <ActivateWebCouponInOutdatedBasket />
        </ErrorBoundary>
        <ConditionalFraudDetection />
        <DatadogLogsRouteTracker />
        <BrowserMemoryConsumptionTracker />
        <InitializeAbTestsConditionally />
        <SubscribeToWhatsappNewsletter />
        <CheckoutPreloadConditional />
        <GuestCheckoutSessionRemover />
        <PageReadyHint />
        <ErrorBoundary
          renderContext="help-center-chatbot"
          ErrorComponent={null}
        >
          <HelpCenterChatbotOpener />
        </ErrorBoundary>
      </ClientGate>
    </AppProviders>
  );
};

const ConditionalFraudDetection = () => {
  const isHydrationDone = useHydrationDone();

  // We need the script injection to only happen after hydration to get the
  // real cookie value
  if (!isHydrationDone) {
    return null;
  }

  return <FraudDetection />;
};

const ConditionalUspBanner = () => {
  const isSpecialPageWithoutUspBanner = useIsSpecialPageWithoutUspBanner();
  const isCheckoutPage = useIsCheckoutPage();

  if (isSpecialPageWithoutUspBanner || isCheckoutPage) {
    return null;
  }
  return <UspBanner />;
};

const ConditionalPhonePopup = () => {
  const isHydrationDone = useHydrationDone();
  const isBasketPage = useIsBasketPage();
  if (!isHydrationDone || isBasketPage) {
    return null;
  }
  return <PhonePopupConditional />;
};

const ConditionalHeader = () => {
  const {isHeaderDisabled} = useFullScreenView();
  if (isHeaderDisabled) {
    return null;
  }
  return (
    <ErrorBoundary renderContext="header">
      <Header />
    </ErrorBoundary>
  );
};

const ConditionalCouponTopBanner = () => {
  const isCheckoutPage = useIsCheckoutPage();
  if (isCheckoutPage) {
    return null;
  }
  return <CouponTopBanner />;
};

const ConditionalDiscountBanner = () => {
  const isCheckoutPage = useIsCheckoutPage();
  if (isCheckoutPage) {
    return null;
  }
  return <DiscountBanner />;
};

const ConditionalFooter = () => {
  const {isFooterDisabled} = useFullScreenView();
  if (isFooterDisabled) {
    return null;
  }
  return (
    <ErrorBoundary renderContext="footer">
      <Footer />
    </ErrorBoundary>
  );
};

const CountryDetectionConditionally = () => {
  const isInternationalPopupEnabled = useIsSoftFeatureToggleActive(
    'show_international_popup',
  );

  const history = useHistory();
  const params = new URLSearchParams(history.location.search);

  const isDisabledByUrlParam = useRef(
    params.get('disableCountrySwitch') === '1',
  ).current;

  const enteredThroughOSP = useRef(useIsOrderSuccessPage()).current;

  return isInternationalPopupEnabled &&
    !enteredThroughOSP &&
    !isDisabledByUrlParam ? (
    <CountryDetection />
  ) : null;
};

const ConditionalSlideInWebCouponBanner = () => {
  const shouldShowSlideInWebCouponBanner = useShowSlideInWebCouponBanner();
  return shouldShowSlideInWebCouponBanner ? <SlideInWebCouponBanner /> : null;
};
